<template>
	<div>
		<PasswordInput
			v-model="password"
			:label="label"
			v-bind="$attrs"
			:transparent="transparent"
			@blur="$emit('blur')" />
		<div class="password-req-card">
			<div class="password-req-header">Your password must contain:</div>
			<ul class="password-requirements mb-3 ps-0 small">
				<li
					v-for="(req, i) in requirements"
					:key="i"
					:class="{ 'text-success': req.met, 'text-muted': !req.met }">
					<span class="me-2">
						<i v-if="req.met" class="bi bi-check-circle-fill"></i>
						<i v-else class="bi bi-circle"></i>
					</span>
					<span :class="{ 'fw-bold': req.met }">{{ req.label }}</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup>
	import { ref, computed, watch } from "vue"
	import PasswordInput from "./PasswordInput.vue"

	const props = defineProps({
		modelValue: String,
		label: {
			type: String,
			default: "Password",
		},
		transparent: {
			type: Boolean,
			default: false,
		},
	})
	const emit = defineEmits(["update:modelValue", "valid", "blur"])

	const password = ref(props.modelValue || "")

	watch(
		() => props.modelValue,
		(val) => {
			if (val !== password.value) password.value = val
		}
	)
	watch(password, (val) => {
		emit("update:modelValue", val)
	})

	const requirements = computed(() => {
		const val = password.value || ""
		return [
			{
				label: "10 or more characters",
				met: val.length >= 10,
			},
			{
				label: "Lowercase letters (a-z)",
				met: /[a-z]/.test(val),
			},
			{
				label: "Uppercase letters (A-Z)",
				met: /[A-Z]/.test(val),
			},
			{
				label: "Numbers (0-9)",
				met: /[0-9]/.test(val),
			},
			{
				label: "Special characters (ex. !@#)",
				met: /[^A-Za-z0-9]/.test(val),
			},
		]
	})

	const isValid = computed(() => requirements.value.every((r) => r.met))

	watch(
		() => password.value,
		() => {
			emit("valid", isValid.value)
		},
		{ immediate: true }
	)
</script>

<style scoped>
	.password-req-card {
		background: #fff;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		padding: 1rem 1.5rem 0.5rem 1.5rem;
		margin-bottom: 1rem;
		max-width: 350px;
	}
	.password-req-header {
		font-weight: 600;
		margin-bottom: 0.5rem;
	}
	.password-requirements {
		list-style: none;
		padding-left: 0;
		margin-bottom: 1rem;
		margin-top: 0.25rem;
	}
	.password-requirements li {
		display: flex;
		align-items: center;
		margin-bottom: 0.25rem;
		font-size: 0.98em;
	}
	.text-success {
		color: #198754 !important;
	}
	.text-muted {
		color: #adb5bd !important;
	}
	.fw-bold {
		font-weight: 600 !important;
	}
</style>
